import { Formik, Form } from "formik";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useAuth } from "@/core/auth";
import { useIsMobile } from "@/core/component-hooks/use-mobile";
import { cn } from "@/core/utils/utils";

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const isMobile = useIsMobile();

  const [showPassword, setShowPassword] = useState(false);

  return (
    <Stack
      direction="row"
      align="center"
      justify="center"
      fullWidth
      className={cn("min-h-[calc(100vh-67px)] overflow-hidden p-6", {
        "overflow-y-auto": isMobile,
        "p-4": isMobile,
      })}
    >
      <Card
        className={cn("w-full max-w-md p-6 pt-8", {
          "p-4 pt-5": isMobile,
        })}
      >
        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={loginSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await login(values);
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Login failed, lease try again",
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ getFieldProps, isSubmitting, isValid }) => (
            <Form>
              <Stack
                className={cn("gap-y-10", {
                  "gap-y-6": isMobile,
                })}
                fullWidth
              >
                <Typography
                  variant="subtitleW1"
                  className={cn("mt-4", {
                    "mt-2": isMobile,
                  })}
                >
                  Login to your account
                </Typography>

                <Stack gap={isMobile ? "md" : "lg"} className="pb-4" fullWidth>
                  <FormInput
                    id="email"
                    type="email"
                    label="Email"
                    placeholder="Enter your email"
                    {...getFieldProps("email")}
                    required
                    disableRequirementAsterisk
                    fullWidth
                  />

                  <FormInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    placeholder="Enter your password"
                    {...getFieldProps("password")}
                    fullWidth
                    required
                    disableRequirementAsterisk
                    RightActionComponent={
                      <Typography
                        variant="subtitleW3"
                        textColor="interactive"
                        className="mt-5 cursor-pointer self-end"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </Typography>
                    }
                  />
                </Stack>

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  disabled={!isValid}
                  leftIcon={
                    isSubmitting && <Loader2 className="animate-spin" />
                  }
                >
                  Sign In
                </Button>

                <Stack
                  direction={isMobile ? "column" : "row"}
                  gap="sm"
                  align={isMobile ? "start" : "center"}
                  justify={isMobile ? "start" : "end"}
                  fullWidth
                >
                  <Typography
                    variant="subtitleW3"
                    textColor="interactive"
                    className={cn("cursor-pointer self-end", {
                      "self-start": isMobile,
                    })}
                    onClick={() => navigate("/signup")}
                  >
                    Sign up
                  </Typography>
                  {!isMobile && (
                    <Separator orientation="vertical" className="h-4" />
                  )}
                  <Typography
                    variant="subtitleW3"
                    textColor="interactive"
                    className={cn("cursor-pointer self-end", {
                      "self-start": isMobile,
                    })}
                    onClick={() => navigate("/forgot-password")}
                  >
                    Forgot password
                  </Typography>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
